// src/pages/Convocations/utils/convocation.utils.js

export const cx = (...classes) =>
  classes.filter(Boolean).join(" ");

export const getStoredUser = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedUser =
      localStorage.getItem("userInfo");

    return storedUser
      ? JSON.parse(storedUser)
      : null;
  } catch (error) {
    console.error(
      "Impossible de lire userInfo.",
      error,
    );

    return null;
  }
};

export const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const toValidDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

export const formatDate = (value) => {
  const date = toValidDate(value);

  if (!date) {
    return "—";
  }

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const formatDateLong = (value) => {
  const date = toValidDate(value);

  if (!date) {
    return "Date non renseignée";
  }

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const formatDateTime = (value) => {
  const date = toValidDate(value);

  if (!date) {
    return "—";
  }

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatTime = (value) => {
  const date = toValidDate(value);

  if (!date) {
    return "—";
  }

  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const normalizeResponseStatus = (
  status,
) => {
  const normalizedStatus = normalizeText(
    status || "pending",
  );

  if (normalizedStatus === "accepted") {
    return "accepted";
  }

  if (normalizedStatus === "rejected") {
    return "rejected";
  }

  return "pending";
};

export const getEventPivot = (event) =>
  event?.ActeursEvents ||
  event?.ActeurEvent ||
  event?.ActeursEvent ||
  {};

export const normalizeConvocationsFromActor = (
  actorData,
) => {
  if (!actorData) {
    return [];
  }

  const events = Array.isArray(actorData.Events)
    ? actorData.Events
    : [];

  return events.map((event) => {
    const pivot = getEventPivot(event);

    return {
      id:
        pivot.id ??
        `${actorData.id}-${event?.id}`,

      acteur_id: actorData.id,
      event_id: event?.id,

      poste: pivot.poste ?? "",
      tapis: pivot.tapis ?? "",

      is_validate:
        pivot.is_validate === true ||
        pivot.is_validate === 1,

      note: pivot.note ?? null,
      observations:
        pivot.observations ?? "",

      response_status:
        normalizeResponseStatus(
          pivot.response_status,
        ),

      response_reason:
        pivot.response_reason ?? "",

      responded_at:
        pivot.responded_at ?? null,

      need_transport_support:
        pivot.need_transport_support ?? null,

      need_accommodation_support:
        pivot.need_accommodation_support ?? null,

      support_request_comment:
        pivot.support_request_comment ?? "",

      support_requested_at:
        pivot.support_requested_at ?? null,

      transport_support_status:
        pivot.transport_support_status ?? null,

      accommodation_support_status:
        pivot.accommodation_support_status ??
        null,

      Event: event,

      acteur: {
        id: actorData.id,
        prenom: actorData.prenom,
        nom: actorData.nom,
        club_acteur:
          actorData.club_acteur,
      },
    };
  });
};

export const getActorLabel = (actor) => {
  const firstName = actor?.prenom || "";
  const lastName = actor?.nom || "";

  return (
    `${firstName} ${lastName}`.trim() ||
    "Profil acteur"
  );
};

export const getConvocationActorLabel = (
  convocation,
) => {
  return getActorLabel(
    convocation?.acteur,
  );
};

export const getEventLocation = (event) =>
  event?.lieu_event ||
  event?.adresse ||
  "Lieu non renseigné";

export const getEventReferenceDate = (
  event,
) => {
  const endDate = toValidDate(event?.end);

  if (endDate) {
    return endDate;
  }

  const startDate = toValidDate(
    event?.start,
  );

  if (!startDate) {
    return null;
  }

  /*
   * Sans date de fin, l'événement reste
   * considéré comme actuel jusqu'à la fin
   * de sa journée.
   */
  const endOfDay = new Date(startDate);

  endOfDay.setHours(
    23,
    59,
    59,
    999,
  );

  return endOfDay;
};

export const isEventUpcoming = (event) => {
  const referenceDate =
    getEventReferenceDate(event);

  if (!referenceDate) {
    return true;
  }

  return (
    referenceDate.getTime() >=
    Date.now()
  );
};

export const getSupportStatusLabel = (
  status,
) => {
  switch (status) {
    case "approved":
      return "Validée";

    case "rejected":
      return "Refusée";

    case "pending":
      return "En attente";

    default:
      return "En attente";
  }
};

export const getConvocationStats = (
  convocations,
) => {
  const list = Array.isArray(
    convocations,
  )
    ? convocations
    : [];

  return list.reduce(
    (stats, convocation) => {
      const status =
        normalizeResponseStatus(
          convocation?.response_status,
        );

      stats.total += 1;
      stats[status] += 1;

      if (
        status === "pending" &&
        isEventUpcoming(
          convocation?.Event,
        )
      ) {
        stats.pendingFuture += 1;
      }

      return stats;
    },
    {
      total: 0,
      pending: 0,
      pendingFuture: 0,
      accepted: 0,
      rejected: 0,
    },
  );
};

export const getUniqueSortedValues = (
  items,
  getter,
) => {
  const values = new Set();

  const safeItems = Array.isArray(items)
    ? items
    : [];

  safeItems.forEach((item) => {
    const value = getter(item);

    if (value) {
      values.add(value);
    }
  });

  return Array.from(values).sort(
    (valueA, valueB) =>
      String(valueA).localeCompare(
        String(valueB),
        "fr",
        {
          sensitivity: "base",
        },
      ),
  );
};

const getEventTimestamp = (
  convocation,
) => {
  const eventDate = toValidDate(
    convocation?.Event?.start,
  );

  return eventDate
    ? eventDate.getTime()
    : null;
};

export const filterAndSortConvocations = (
  convocations,
  filters,
) => {
  const {
    searchQuery,
    selectedType,
    selectedPoste,
    selectedStatus,
    selectedActorId,
    showOnlyFuture,
    sortBy,
  } = filters;

  let list = [
    ...(Array.isArray(convocations)
      ? convocations
      : []),
  ];

  if (showOnlyFuture) {
    list = list.filter(
      (convocation) =>
        isEventUpcoming(
          convocation?.Event,
        ),
    );
  }

  if (selectedType) {
    list = list.filter(
      (convocation) =>
        convocation?.Event
          ?.type_event ===
        selectedType,
    );
  }

  if (selectedPoste) {
    list = list.filter(
      (convocation) =>
        convocation?.poste ===
        selectedPoste,
    );
  }

  if (selectedStatus) {
    list = list.filter(
      (convocation) =>
        normalizeResponseStatus(
          convocation?.response_status,
        ) === selectedStatus,
    );
  }

  if (selectedActorId) {
    list = list.filter(
      (convocation) =>
        String(
          convocation?.acteur_id,
        ) ===
        String(selectedActorId),
    );
  }

  const normalizedQuery =
    normalizeText(searchQuery);

  if (normalizedQuery) {
    list = list.filter(
      (convocation) => {
        const event =
          convocation?.Event || {};

        const searchableContent =
          normalizeText(
            [
              event.titre,
              event.type_event,
              event.lieu_event,
              event.adresse,
              convocation.poste,
              convocation.tapis,
              convocation.response_status,
              convocation.acteur
                ?.prenom,
              convocation.acteur?.nom,
              convocation.acteur
                ?.club_acteur,
            ]
              .filter(Boolean)
              .join(" "),
          );

        return searchableContent.includes(
          normalizedQuery,
        );
      },
    );
  }

  const statusWeight = {
    pending: 0,
    accepted: 1,
    rejected: 2,
  };

  list.sort(
    (convocationA, convocationB) => {
      const timestampA =
        getEventTimestamp(
          convocationA,
        );

      const timestampB =
        getEventTimestamp(
          convocationB,
        );

      /*
       * Les événements sans date sont
       * toujours placés à la fin.
       */
      if (
        timestampA === null &&
        timestampB !== null
      ) {
        return 1;
      }

      if (
        timestampA !== null &&
        timestampB === null
      ) {
        return -1;
      }

      if (
        timestampA === null &&
        timestampB === null
      ) {
        return 0;
      }

      switch (sortBy) {
        case "date-desc":
          return (
            timestampB - timestampA
          );

        case "title-asc":
          return String(
            convocationA?.Event
              ?.titre || "",
          ).localeCompare(
            String(
              convocationB?.Event
                ?.titre || "",
            ),
            "fr",
            {
              sensitivity: "base",
            },
          );

        case "status": {
          const statusA =
            normalizeResponseStatus(
              convocationA
                ?.response_status,
            );

          const statusB =
            normalizeResponseStatus(
              convocationB
                ?.response_status,
            );

          const statusDifference =
            statusWeight[statusA] -
            statusWeight[statusB];

          return (
            statusDifference ||
            timestampA - timestampB
          );
        }

        case "date-asc":
        default:
          return (
            timestampA - timestampB
          );
      }
    },
  );

  return list;
};

export const getActiveFilterCount = (
  filters,
) => {
  let count = 0;

  if (filters.searchQuery.trim()) {
    count += 1;
  }

  if (filters.selectedType) {
    count += 1;
  }

  if (filters.selectedPoste) {
    count += 1;
  }

  if (filters.selectedStatus) {
    count += 1;
  }

  if (filters.selectedActorId) {
    count += 1;
  }

  /*
   * true est la valeur par défaut.
   */
  if (!filters.showOnlyFuture) {
    count += 1;
  }

  if (
    filters.sortBy !== "date-asc"
  ) {
    count += 1;
  }

  return count;
};